import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Dropdown, Navbar, Button, Image, Modal, Spinner, Card } from 'react-bootstrap';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { Info, HeartFill, Folder, Phone, Newspaper, Building, FileText, List, InfoCircle, FolderPlus } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { getCountryInfo, getActiveCountries } from '../../utils/countryConfig';
import { getStateInfo } from '../../utils/feedsConfig';
import MobileNavigation from '../navigation/MobileNavigation';
import ReactMarkdown from 'react-markdown';
import openCapacitorSite from '../../utils/browserUtils';

// Helper to clean AI preamble and follow-up suggestions from content
function cleanAISuggestions(content) {
  if (!content) return content;
  let cleaned = content;
  const preamblePatterns = [
    /^Here (?:are|is) (?:some|a|the)[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^Below (?:are|is)[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^I found[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^Based on[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^The following[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^For official[\s\S]*?(?:\.\[\d+\])+\s*/i,
    /^For (?:local|city|government|federal|state)[\s\S]*?(?:\.\[\d+\])+\s*/i,
  ];
  const suggestionPatterns = [
    /If you(?:'d like| want),? (?:I can|you can|let me know)[\s\S]*$/i,
    /Would you like me to[\s\S]*$/i,
    /Let me know if you(?:'d like| want)[\s\S]*$/i,
    /I can (?:also |further )?(?:focus|narrow|filter|provide|help|drill)[\s\S]*$/i,
    /Do you want me to[\s\S]*$/i,
    /Want me to[\s\S]*$/i,
    /If you need[\s\S]*$/i,
    /If there's a specific[\s\S]*$/i,
    /If there is a specific[\s\S]*$/i,
    /You can also[\s\S]*$/i,
    /For (?:more|additional|further) (?:details|information|updates)[\s\S]*$/i,
    /If you(?:'d like| want| need) (?:the latest|more|to|me to)[\s\S]*$/i,
  ];
  for (const pattern of preamblePatterns) { cleaned = cleaned.replace(pattern, '').trim(); }
  for (const pattern of suggestionPatterns) { cleaned = cleaned.replace(pattern, '').trim(); }
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  return cleaned;
}

const processCitationsInContent = (content, citations) => {
  if (!content || !citations || citations.length === 0) return content;
  let processed = content;
  citations.forEach((citation, index) => {
    const citationNumber = index + 1;
    const pattern = new RegExp(`\\[${citationNumber}\\]`, 'g');
    if (citation.url) {
      processed = processed.replace(pattern, `[[${citationNumber}]](${citation.url})`);
    }
  });
  return processed;
};

const MarkdownLink = ({ href, children }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openCapacitorSite(href, '_blank');
  };
  return <a href={href} onClick={handleClick} style={{ cursor: 'pointer' }}>{children}</a>;
};

const ReportContent = ({ content, citations, className = '' }) => {
  const processedContent = processCitationsInContent(content, citations);
  return (
    <div className={`report-markdown-content ${className}`}>
      <ReactMarkdown components={{ a: MarkdownLink }}>{processedContent}</ReactMarkdown>
    </div>
  );
};

// US States Data
const US_STATES_DATA = [
  { name: "Alabama", flag: "/alabamaFlag.png", path: "/us/alabama", key: "alabama" },
  { name: "Alaska", flag: "/alaskaFlag.png", path: "/us/alaska", key: "alaska" },
  { name: "Arizona", flag: "/arizonaFlag.png", path: "/us/arizona", key: "arizona" },
  { name: "Arkansas", flag: "/arkansasFlag.png", path: "/us/arkansas", key: "arkansas" },
  { name: "California", flag: "/californiaFlag.png", path: "/us/california", key: "california" },
  { name: "Colorado", flag: "/coloradoFlag.png", path: "/us/colorado", key: "colorado" },
  { name: "Connecticut", flag: "/connecticutFlag.png", path: "/us/connecticut", key: "connecticut" },
  { name: "Delaware", flag: "/delawareFlag.png", path: "/us/delaware", key: "delaware" },
  { name: "Florida", flag: "/floridaFlag.png", path: "/us/florida", key: "florida" },
  { name: "Georgia", flag: "/georgiaFlag.png", path: "/us/georgia", key: "georgia" },
  { name: "Hawaii", flag: "/hawaiiFlag.png", path: "/us/hawaii", key: "hawaii" },
  { name: "Idaho", flag: "/idahoFlag.png", path: "/us/idaho", key: "idaho" },
  { name: "Illinois", flag: "/illinoisFlag.png", path: "/us/illinois", key: "illinois" },
  { name: "Indiana", flag: "/indianaFlag.png", path: "/us/indiana", key: "indiana" },
  { name: "Iowa", flag: "/iowaFlag.png", path: "/us/iowa", key: "iowa" },
  { name: "Kansas", flag: "/kansasFlag.png", path: "/us/kansas", key: "kansas" },
  { name: "Kentucky", flag: "/kentuckyFlag.png", path: "/us/kentucky", key: "kentucky" },
  { name: "Louisiana", flag: "/louisianaFlag.png", path: "/us/louisiana", key: "louisiana" },
  { name: "Maine", flag: "/maineFlag.png", path: "/us/maine", key: "maine" },
  { name: "Maryland", flag: "/marylandFlag.png", path: "/us/maryland", key: "maryland" },
  { name: "Massachusetts", flag: "/massachusettsFlag.png", path: "/us/massachusetts", key: "massachusetts" },
  { name: "Michigan", flag: "/michiganFlag.png", path: "/us/michigan", key: "michigan" },
  { name: "Minnesota", flag: "/minnesotaFlag.png", path: "/us/minnesota", key: "minnesota" },
  { name: "Mississippi", flag: "/mississippiFlag.png", path: "/us/mississippi", key: "mississippi" },
  { name: "Missouri", flag: "/missouriFlag.png", path: "/us/missouri", key: "missouri" },
  { name: "Montana", flag: "/montanaFlag.png", path: "/us/montana", key: "montana" },
  { name: "Nebraska", flag: "/nebraskaFlag.png", path: "/us/nebraska", key: "nebraska" },
  { name: "Nevada", flag: "/nevadaFlag.png", path: "/us/nevada", key: "nevada" },
  { name: "New Hampshire", flag: "/newHampshireFlag.png", path: "/us/newhampshire", key: "newhampshire" },
  { name: "New Jersey", flag: "/newJerseyFlag.png", path: "/us/newjersey", key: "newjersey" },
  { name: "New Mexico", flag: "/newMexicoFlag.png", path: "/us/newmexico", key: "newmexico" },
  { name: "New York", flag: "/newYorkFlag.png", path: "/us/newyork", key: "newyork" },
  { name: "North Carolina", flag: "/northCarolinaFlag.png", path: "/us/northcarolina", key: "northcarolina" },
  { name: "North Dakota", flag: "/northDakotaFlag.png", path: "/us/northdakota", key: "northdakota" },
  { name: "Ohio", flag: "/ohioFlag.png", path: "/us/ohio", key: "ohio" },
  { name: "Oklahoma", flag: "/oklahomaFlag.png", path: "/us/oklahoma", key: "oklahoma" },
  { name: "Oregon", flag: "/oregonFlag.png", path: "/us/oregon", key: "oregon" },
  { name: "Pennsylvania", flag: "/pennsylvaniaFlag.png", path: "/us/pennsylvania", key: "pennsylvania" },
  { name: "Rhode Island", flag: "/rhodeIslandFlag.png", path: "/us/rhodeisland", key: "rhodeisland" },
  { name: "South Carolina", flag: "/southCarolinaFlag.png", path: "/us/southcarolina", key: "southcarolina" },
  { name: "South Dakota", flag: "/southDakotaFlag.png", path: "/us/southdakota", key: "southdakota" },
  { name: "Tennessee", flag: "/tennesseeFlag.png", path: "/us/tennessee", key: "tennessee" },
  { name: "Texas", flag: "/texasFlag.png", path: "/us/texas", key: "texas" },
  { name: "Utah", flag: "/utahFlag.png", path: "/us/utah", key: "utah" },
  { name: "Vermont", flag: "/vermontFlag.png", path: "/us/vermont", key: "vermont" },
  { name: "Virginia", flag: "/virginiaFlag.png", path: "/us/virginia", key: "virginia" },
  { name: "Washington", flag: "/washingtonFlag.png", path: "/us/washington", key: "washington" },
  { name: "West Virginia", flag: "/westVirginiaFlag.png", path: "/us/westvirginia", key: "westvirginia" },
  { name: "Wisconsin", flag: "/wisconsinFlag.png", path: "/us/wisconsin", key: "wisconsin" },
  { name: "Wyoming", flag: "/wyomingFlag.png", path: "/us/wyoming", key: "wyoming" }
];

const CANADA_PROVINCES_DATA = [
  { name: "Alberta", flag: "/albertaFlag.png", path: "/canada/alberta", key: "alberta" },
  { name: "British Columbia", flag: "/britishColumbiaFlag.png", path: "/canada/british_columbia", key: "british_columbia" },
  { name: "Manitoba", flag: "/manitobaFlag.png", path: "/canada/manitoba", key: "manitoba" },
  { name: "New Brunswick", flag: "/newBrunswickFlag.png", path: "/canada/new_brunswick", key: "new_brunswick" },
  { name: "Newfoundland & Labrador", flag: "/newfoundlandAndLabradorFlag.png", path: "/canada/newfoundland_labrador", key: "newfoundland_labrador" },
  { name: "Northwest Territories", flag: "/northwestTerritoriesFlag.png", path: "/canada/northwest_territories", key: "northwest_territories" },
  { name: "Nova Scotia", flag: "/novaScotiaFlag.png", path: "/canada/nova_scotia", key: "nova_scotia" },
  { name: "Nunavut", flag: "/nunavutFlag.png", path: "/canada/nunavut", key: "nunavut" },
  { name: "Ontario", flag: "/ontarioFlag.png", path: "/canada/ontario", key: "ontario" },
  { name: "Prince Edward Island", flag: "/princeEdwardIslandFlag.png", path: "/canada/prince_edward_island", key: "prince_edward_island" },
  { name: "Quebec", flag: "/quebecFlag.png", path: "/canada/quebec", key: "quebec" },
  { name: "Saskatchewan", flag: "/saskatchewanFlag.png", path: "/canada/saskatchewan", key: "saskatchewan" },
  { name: "Yukon", flag: "/yukonFlag.png", path: "/canada/yukon", key: "yukon" }
];

const MEXICO_STATES_DATA = [
  { name: "Aguascalientes", flag: "/aguascalientesFlag.png", path: "/mexico/aguascalientes", key: "aguascalientes" },
  { name: "Baja California", flag: "/bajaCaliforniaFlag.png", path: "/mexico/bajacalifornia", key: "bajacalifornia" },
  { name: "Baja California Sur", flag: "/bajaCaliforniaSurFlag.png", path: "/mexico/bajacaliforniasur", key: "bajacaliforniasur" },
  { name: "Campeche", flag: "/campecheFlag.png", path: "/mexico/campeche", key: "campeche" },
  { name: "Chiapas", flag: "/chiapasFlag.png", path: "/mexico/chiapas", key: "chiapas" },
  { name: "Chihuahua", flag: "/chihuahuaFlag.png", path: "/mexico/chihuahua", key: "chihuahua" },
  { name: "Coahuila", flag: "/coahuilaFlag.png", path: "/mexico/coahuila", key: "coahuila" },
  { name: "Colima", flag: "/colimaFlag.png", path: "/mexico/colima", key: "colima" },
  { name: "Durango", flag: "/durangoFlag.png", path: "/mexico/durango", key: "durango" },
  { name: "Guanajuato", flag: "/guanajuatoFlag.png", path: "/mexico/guanajuato", key: "guanajuato" },
  { name: "Guerrero", flag: "/guerreroFlag.png", path: "/mexico/guerrero", key: "guerrero" },
  { name: "Hidalgo", flag: "/hidalgoFlag.png", path: "/mexico/hidalgo", key: "hidalgo" },
  { name: "Jalisco", flag: "/jaliscoFlag.png", path: "/mexico/jalisco", key: "jalisco" },
  { name: "Ciudad de México", flag: "/mexicoCityFlag.png", path: "/mexico/mexicocity", key: "mexicocity" },
  { name: "Estado de México", flag: "/mexicoStateFlag.png", path: "/mexico/mexicostate", key: "mexicostate" },
  { name: "Michoacán", flag: "/michoacanFlag.png", path: "/mexico/michoacan", key: "michoacan" },
  { name: "Morelos", flag: "/morelosFlag.png", path: "/mexico/morelos", key: "morelos" },
  { name: "Nayarit", flag: "/nayaritFlag.png", path: "/mexico/nayarit", key: "nayarit" },
  { name: "Nuevo León", flag: "/nuevoLeonFlag.png", path: "/mexico/nuevoleon", key: "nuevoleon" },
  { name: "Oaxaca", flag: "/oaxacaFlag.png", path: "/mexico/oaxaca", key: "oaxaca" },
  { name: "Puebla", flag: "/pueblaFlag.png", path: "/mexico/puebla", key: "puebla" },
  { name: "Querétaro", flag: "/queretaroFlag.png", path: "/mexico/queretaro", key: "queretaro" },
  { name: "Quintana Roo", flag: "/quintanaRooFlag.png", path: "/mexico/quintanaroo", key: "quintanaroo" },
  { name: "San Luis Potosí", flag: "/sanLuisPotosiFlag.png", path: "/mexico/sanluispotosi", key: "sanluispotosi" },
  { name: "Sinaloa", flag: "/sinaloaFlag.png", path: "/mexico/sinaloa", key: "sinaloa" },
  { name: "Sonora", flag: "/sonoraFlag.png", path: "/mexico/sonora", key: "sonora" },
  { name: "Tabasco", flag: "/tabascoFlag.png", path: "/mexico/tabasco", key: "tabasco" },
  { name: "Tamaulipas", flag: "/tamaulipasFlag.png", path: "/mexico/tamaulipas", key: "tamaulipas" },
  { name: "Tlaxcala", flag: "/tlaxcalaFlag.png", path: "/mexico/tlaxcala", key: "tlaxcala" },
  { name: "Veracruz", flag: "/veracruzFlag.png", path: "/mexico/veracruz", key: "veracruz" },
  { name: "Yucatán", flag: "/yucatanFlag.png", path: "/mexico/yucatan", key: "yucatan" },
  { name: "Zacatecas", flag: "/zacatecasFlag.png", path: "/mexico/zacatecas", key: "zacatecas" }
];

const UK_KINGDOMS_DATA = [
  { name: "England", flag: "/englandFlag.png", path: "/uk/england", key: "england" },
  { name: "Scotland", flag: "/scotlandFlag.png", path: "/uk/scotland", key: "scotland" },
  { name: "Wales", flag: "/walesFlag.png", path: "/uk/wales", key: "wales" },
  { name: "Northern Ireland", flag: "/northernIrelandFlag.png", path: "/uk/northernireland", key: "northernireland" }
];

const detectRegionFromPath = (path) => {
  if (path.startsWith('/us/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment && pathSegment !== 'local') {
      const state = US_STATES_DATA.find(s => s.key === pathSegment || s.path === `/us/${pathSegment}`);
      if (state) return { region: state, country: 'us', level: 'state' };
    }
  }
  if (path.startsWith('/canada/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment) {
      const province = CANADA_PROVINCES_DATA.find(p => p.key === pathSegment || p.path === `/canada/${pathSegment}`);
      if (province) return { region: province, country: 'canada', level: 'province' };
    }
  }
  if (path.startsWith('/mexico/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment) {
      const state = MEXICO_STATES_DATA.find(s => s.key === pathSegment || s.path === `/mexico/${pathSegment}`);
      if (state) return { region: state, country: 'mexico', level: 'state' };
    }
  }
  if (path.startsWith('/uk/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment) {
      const kingdom = UK_KINGDOMS_DATA.find(k => k.key === pathSegment || k.path === `/uk/${pathSegment}`);
      if (kingdom) return { region: kingdom, country: 'uk', level: 'kingdom' };
    }
  }
  return null;
};

const SearchNavBar = ({ pageCurrentCountry = 'us' }) => {
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Measured navbar height — drives the spacer so content sits flush below
  // the fixed navbar regardless of whether the level switcher is showing.
  const navbarRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);

  const getInitialCountry = () => {
    const storedCountry = localStorage.getItem('selectedCountry') || localStorage.getItem('homeCountry');
    return storedCountry || pageCurrentCountry;
  };
  const [currentCountry, setCurrentCountry] = useState(getInitialCountry);
  
  const getInitialLevel = () => {
    return localStorage.getItem('selectedLevel') || 'federal';
  };
  const [currentLevel, setCurrentLevel] = useState(getInitialLevel);
  
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [isOnLocalPage, setIsOnLocalPage] = useState(false);
  
  const [homeStateInfo, setHomeStateInfo] = useState(null);
  const [homeCityName, setHomeCityName] = useState(null);
  const [homeCityAbbreviation, setHomeCityAbbreviation] = useState(null);
  const [homeProvinceInfo, setHomeProvinceInfo] = useState(null);
  const [homeMexicoStateInfo, setHomeMexicoStateInfo] = useState(null);
  const [homeKingdomInfo, setHomeKingdomInfo] = useState(null);

  const [citySearchModalOpen, setCitySearchModalOpen] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReportCity, setSelectedReportCity] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [reportModalView, setReportModalView] = useState('summary');
  
  useEffect(() => {
    const checkHomeLocation = () => {
      const savedHomeState = localStorage.getItem('homeState');
      const savedHomeCityData = localStorage.getItem('homeCityData');
      if (savedHomeState) {
        const stateInfo = getStateInfo(savedHomeState);
        setHomeStateInfo(stateInfo ? { key: savedHomeState, ...stateInfo } : null);
      } else { setHomeStateInfo(null); }
      if (savedHomeCityData) {
        try {
          const cityData = JSON.parse(savedHomeCityData);
          setHomeCityName(cityData.name || null);
          if (cityData.name) {
            const abbrev = cityData.name.substring(0, 3).toUpperCase();
            setHomeCityAbbreviation(abbrev);
          } else { setHomeCityAbbreviation(null); }
        } catch (e) { setHomeCityName(null); setHomeCityAbbreviation(null); }
      } else { setHomeCityName(null); setHomeCityAbbreviation(null); }
      const savedHomeProvince = localStorage.getItem('homeProvince');
      if (savedHomeProvince) {
        try { const provinceData = JSON.parse(savedHomeProvince); setHomeProvinceInfo(provinceData); }
        catch (e) { setHomeProvinceInfo({ key: savedHomeProvince, name: savedHomeProvince }); }
      } else { setHomeProvinceInfo(null); }
      const savedMexicoState = localStorage.getItem('homeMexicanState') || localStorage.getItem('homeMexicoState');
      if (savedMexicoState) {
        const mexicoStateData = MEXICO_STATES_DATA.find(s => s.key === savedMexicoState);
        if (mexicoStateData) { setHomeMexicoStateInfo(mexicoStateData); }
        else {
          try { const stateData = JSON.parse(savedMexicoState); setHomeMexicoStateInfo(stateData); }
          catch (e) { setHomeMexicoStateInfo({ key: savedMexicoState, name: savedMexicoState }); }
        }
      } else { setHomeMexicoStateInfo(null); }
      const savedHomeKingdom = localStorage.getItem('homeUKRegion') || localStorage.getItem('homeKingdom');
      if (savedHomeKingdom) {
        const kingdomData = UK_KINGDOMS_DATA.find(k => k.key === savedHomeKingdom);
        if (kingdomData) { setHomeKingdomInfo(kingdomData); }
        else {
          try { const parsed = JSON.parse(savedHomeKingdom); setHomeKingdomInfo(parsed); }
          catch (e) { setHomeKingdomInfo({ key: savedHomeKingdom, name: savedHomeKingdom }); }
        }
      } else { setHomeKingdomInfo(null); }
    };
    checkHomeLocation();
    window.addEventListener('storage', checkHomeLocation);
    return () => window.removeEventListener('storage', checkHomeLocation);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const detected = detectRegionFromPath(path);
    if (detected) {
      setSelectedRegion(detected.region);
      setCurrentLevel(detected.level);
      setIsOnLocalPage(false);
      localStorage.setItem('selectedLevel', detected.level);
      if (detected.country !== currentCountry) {
        setCurrentCountry(detected.country);
        localStorage.setItem('selectedCountry', detected.country);
      }
    } else if (path === '/us/local') {
      setCurrentLevel('local');
      setIsOnLocalPage(true);
      localStorage.setItem('selectedLevel', 'local');
      setSelectedRegion(null);
    } else {
      setSelectedRegion(null);
      setIsOnLocalPage(false);
      if (
        path === '/us' || path === '/us/' ||
        path === '/canada' || path === '/canada/' ||
        path === '/mexico' || path === '/mexico/' ||
        path === '/uk' || path === '/uk/'
      ) {
        setCurrentLevel('federal');
        localStorage.setItem('selectedLevel', 'federal');
      }
    }
  }, [location.pathname, currentCountry]);

  const searchCities = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setCitySearchResults([]); setSearchError(null); setShowCityDropdown(false); return;
    }
    setSearchingCities(true); setSearchError(null);
    try {
      const response = await fetch(`https://search.govglance.org/api/geocode?search=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error('Failed to search cities');
      const data = await response.json();
      const validTypes = ['City', 'Town', 'Village', 'Borough', 'Township', 'Municipality', 'Locality'];
      const filteredResults = (data.results || []).filter(result => validTypes.includes(result.type));
      setCitySearchResults(filteredResults);
      setShowCityDropdown(filteredResults.length > 0);
    } catch (error) {
      console.error('City search error:', error);
      setSearchError(t('common:cityError', 'Failed to search cities. Please try again.'));
      setCitySearchResults([]); setShowCityDropdown(false);
    } finally { setSearchingCities(false); }
  }, [t]);

  const handleCitySearchChange = useCallback((e) => {
    const query = e.target.value;
    setCitySearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { searchCities(query); }, 300);
  }, [searchCities]);

  useEffect(() => { return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); }; }, []);

  const handleCitySelectForReport = useCallback(async (city) => {
    setSelectedReportCity(city); setCitySearchModalOpen(false); setCitySearchQuery('');
    setCitySearchResults([]); setShowCityDropdown(false); setReportModalOpen(true);
    setReportLoading(true); setReportData(null); setReportError(null);
    try {
      const localities = [{ name: city.name, state: city.state, type: city.type || 'City' }];
      const localitiesParam = encodeURIComponent(JSON.stringify(localities));
      const queryParam = encodeURIComponent('government news');
      const url = `https://search.govglance.org/api/public-search?query=${queryParam}&localities=${localitiesParam}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const cleanedContent = data.content ? cleanAISuggestions(data.content) : null;
      setReportData({
        city: city.name, state: city.stateName || city.state, type: city.type,
        content: cleanedContent, citations: data.citations || [],
        generatedAt: new Date().toLocaleString()
      });
    } catch (error) {
      console.error('Error fetching city report:', error);
      setReportError(t('common:reportError', 'Failed to generate report. Please try again.'));
    } finally { setReportLoading(false); }
  }, [t]);

  const handleCitySearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); if (citySearchResults.length > 0) handleCitySelectForReport(citySearchResults[0]); }
  }, [citySearchResults, handleCitySelectForReport]);

  const closeCitySearchModal = useCallback(() => {
    setCitySearchModalOpen(false); setCitySearchQuery(''); setCitySearchResults([]);
    setShowCityDropdown(false); setSearchError(null);
  }, []);

  const closeReportModal = useCallback(() => {
    setReportModalOpen(false); setSelectedReportCity(null); setReportData(null);
    setReportError(null); setReportModalView('summary');
  }, []);

  const styles = `
  #navbarMobileTop { padding: 0; }
  #searchContainer { display: flex; flex-wrap: wrap; width: 100%; padding: calc(env(safe-area-inset-top, 0px) + 10px) 10px 8px 10px; }
  #searchCountry .dropdown-flag-img { height: 22px; width: auto; border-radius: 2px; }
  #searchCountry .city-abbreviation { font-size: 12px; font-weight: 600; letter-spacing: 0.5px; }
  #docsDropdownItem { cursor: pointer; font-size: 14px; padding: 8px 16px; display: flex; align-items: center; gap: 8px; }
  #docsDropdownItem:focus, #docsDropdownItem:active, #docsDropdownItem:hover { background-color: var(--accent-hover); color: inherit !important; }
  .dropdown-item-separator { border-top: 1px solid var(--border-secondary); margin: 4px 0; }
  .dropdown-item-special { font-weight: 600; font-size: 15px; }
  .dropdown-region-flag { height: 20px; width: auto; border-radius: 2px; object-fit: contain; }
  #mobileDropdown { padding-top: 2px; flex-shrink: 0; border-color: transparent; }
  .city-report-btn { background-color: var(--search-bg); margin-right: 10px; margin-left: 5px; border-radius: 20px; border: 0px; color: var(--text-primary); display: flex; align-items: center; justify-content: center; min-width: 44px; height: 36px; padding: 4px 10px; cursor: pointer; transition: background-color 0.2s ease; }
  .city-report-btn:hover {  }
  .city-report-btn svg { width: 18px; height: 18px; }
  #navbarMenuButton:hover { background: var(--search-bg); transform: scale(1.05); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
  .navbar-nav-container { display: flex; align-items: center; gap: 10px; }
  .level-switcher-row { width: 100%; display: flex; justify-content: center; padding-top: 4px; padding-left: 10px; padding-right: 10px; }
  .level-switcher { display: flex; align-items: center; width: 100%; justify-content: space-evenly; }
  .level-btn, .level-btn.btn-link { background-color: transparent !important; border: none !important; border-bottom: 2px solid transparent !important; color: var(--text-muted); font-size: 14px; font-weight: 500; padding: 4px 2px; border-radius: 0 !important; line-height: 1.2; box-shadow: none !important; outline: none !important; text-decoration: none !important; -webkit-tap-highlight-color: transparent; }
  .level-btn:hover, .level-btn.btn-link:hover, .level-btn:focus, .level-btn:active, .level-btn:focus:active, .level-btn:focus-visible, .level-btn.btn-link:focus, .level-btn.btn-link:active { background-color: transparent !important; color: var(--text-muted); box-shadow: none !important; outline: none !important; border-bottom: 2px solid transparent !important; text-decoration: none !important; }
  .level-btn.active, .level-btn.btn-link.active { background-color: transparent !important; color: var(--text-primary); font-weight: 600; border-bottom: 2px solid var(--text-primary) !important; }
  .level-btn.active:hover, .level-btn.active:focus, .level-btn.active:active, .level-btn.btn-link.active:hover, .level-btn.btn-link.active:focus, .level-btn.btn-link.active:active { background-color: transparent !important; color: var(--text-primary); box-shadow: none !important; border-bottom: 2px solid var(--text-primary) !important; text-decoration: none !important; }
  .level-btn:disabled, .level-btn.disabled, .level-btn.btn-link:disabled { background-color: transparent !important; color: var(--text-tertiary); border-bottom: 2px solid transparent !important; cursor: not-allowed; opacity: 0.5; text-decoration: none !important; }
  .level-btn:disabled:hover, .level-btn:disabled:focus, .level-btn:disabled:active, .level-btn.disabled:hover, .level-btn.btn-link:disabled:hover, .level-btn.btn-link:disabled:focus { background-color: transparent !important; color: var(--text-tertiary); border-bottom: 2px solid transparent !important; box-shadow: none !important; text-decoration: none !important; }
  #mobileOffcanvas { z-index: 1060 !important; }
  .offcanvas-backdrop { z-index: 1055 !important; }
  .modal { z-index: 1070 !important; }
  .modal-backdrop { z-index: 1065 !important; }

  /* Spacer height is set inline from the measured navbar height (navHeight). */
  .navbar-spacer { width: 100%; flex-shrink: 0; }

  .desktop-nav-buttons { display: none; align-items: center; gap: 8px; margin-left: 10px; }
  .desktop-nav-btn { background-color: var(--search-bg); border: none; border-radius: 20px; color: var(--text-primary); font-size: 13px; padding: 8px 14px; display: flex; align-items: center; gap: 6px; white-space: nowrap; text-decoration: none; }
  .desktop-nav-btn:hover, .desktop-nav-btn:active, .desktop-nav-btn:focus, .desktop-nav-btn:active:focus { background-color: var(--search-focus-bg); color: var(--text-primary); box-shadow: none; outline: none; }
  .desktop-nav-btn svg { width: 14px; height: 14px; }
  .desktop-nav-btn .donate-heart { color: #ff0000; }
  .city-search-modal .modal-content { background-color: var(--bg-primary); border: 1px solid var(--border-primary); }
  .city-search-modal-header { background-color: var(--bg-secondary); color: var(--text-primary); border-color: var(--border-primary); }
  .city-search-modal-header h3 { margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px; }
  .city-search-modal-body { padding: 20px; background-color: var(--bg-primary); min-height: 300px; }
  .city-search-input-wrapper { position: relative; margin-bottom: 20px; }
  .city-search-input { width: 100%; padding: 14px 18px; font-size: 15px; background-color: var(--search-bg); color: var(--text-primary); border: 1px solid var(--border-primary); border-radius: 12px; outline: none; transition: all 0.2s ease; }
  .city-search-input::placeholder { color: var(--text-muted); }
  .city-search-input:focus { border-color: var(--accent-primary); background-color: var(--search-focus-bg); }
  .city-search-input.dropdown-open { border-radius: 12px 12px 0 0; }
  .city-search-loading { position: absolute; right: 15px; top: 50%; transform: translateY(-50%); color: var(--text-muted); font-size: 13px; }
  .city-search-dropdown { position: absolute; top: 100%; left: 0; right: 0; max-height: 280px; overflow-y: auto; background-color: var(--dropdown-bg); border: 1px solid var(--border-primary); border-top: none; border-radius: 0 0 12px 12px; z-index: 100; box-shadow: 0 4px 12px var(--shadow-color); }
  .city-search-dropdown-item { width: 100%; padding: 12px 18px; font-size: 14px; background-color: transparent; color: var(--text-primary); border: none; border-bottom: 1px solid var(--border-secondary); cursor: pointer; text-align: left; display: flex; justify-content: space-between; align-items: center; transition: background-color 0.2s ease; }
  .city-search-dropdown-item:last-child { border-bottom: none; }
  .city-search-dropdown-item:hover { background-color: var(--accent-hover); }
  .city-search-error { margin-bottom: 15px; padding: 10px; background-color: rgba(220, 53, 69, 0.1); border: 1px solid rgba(220, 53, 69, 0.3); border-radius: 10px; text-align: center; color: #dc3545; font-size: 13px; }
  .city-search-helper { text-align: center; color: var(--text-tertiary); font-size: 13px; }
  .report-modal .modal-content { background-color: var(--bg-primary); border: 1px solid var(--border-primary); }
  .report-modal-header { background-color: var(--bg-secondary); color: var(--text-primary); border-color: var(--border-primary); }
  .report-modal-header h3 { margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px; }
  .report-modal-body { padding: 20px; background-color: var(--bg-primary); min-height: 400px; }
  .report-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 16px; color: var(--text-secondary); }
  .report-error { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 16px; color: var(--text-secondary); text-align: center; }
  .report-content { color: var(--text-primary); }
  .report-tab-buttons { display: flex; gap: 10px; margin-bottom: 16px; }
  .report-tab-btn { flex: 1; background-color: var(--bg-card); border: 1px solid var(--border-primary); color: var(--text-primary); padding: 10px 16px; border-radius: 8px; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s ease; }
  .report-tab-btn:hover { background-color: var(--bg-card-hover); color: var(--text-primary); }
  .report-tab-btn.active { background-color: var(--accent-primary); border-color: var(--accent-primary); color: white; }
  .report-meta-info { color: var(--text-secondary); margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--border-secondary); }
  .report-markdown-content { line-height: 1.7; font-size: 15px; }
  .report-markdown-content h1, .report-markdown-content h2, .report-markdown-content h3 { margin-top: 20px; margin-bottom: 10px; font-weight: 600; }
  .report-markdown-content h1 { font-size: 22px; }
  .report-markdown-content h2 { font-size: 18px; }
  .report-markdown-content h3 { font-size: 16px; }
  .report-markdown-content p { margin-bottom: 12px; }
  .report-markdown-content ul, .report-markdown-content ol { margin-bottom: 12px; padding-left: 24px; }
  .report-markdown-content li { margin-bottom: 6px; }
  .report-markdown-content a { color: var(--accent-primary); text-decoration: none; }
  .report-markdown-content a:hover { text-decoration: underline; }
  .report-no-content { color: var(--text-tertiary); text-align: center; padding: 40px 20px; font-style: italic; }
  .report-citation-card { margin-bottom: 12px; }
  .report-citation-card .card { background-color: var(--bg-card); border: 1px solid var(--border-primary); border-radius: 12px; }
  .report-citation-card .card-body { padding: 16px; }
  .citation-title { font-weight: 500; font-size: 15px; color: var(--text-primary); margin-bottom: 6px; }
  .citation-date { font-size: 13px; color: var(--text-secondary); }
  .report-citation-card .card-footer { background-color: transparent; border-top: 1px solid var(--border-secondary); padding: 12px 16px; }
  @media (min-width: 768px) {
    #searchContainer { padding-top: 12px !important; padding-bottom: 8px !important; }
    .desktop-nav-buttons { display: flex; }
    .level-btn { font-size: 14px; padding: 4px 2px; }
    .level-switcher { gap: 20px; }
    .level-switcher-row { padding-top: 4px; }
  }
  @media (min-width: 992px) {
    .desktop-nav-btn { padding: 8px 16px; font-size: 14px; }
  }
  [data-theme="daylight"] #docsDropdownItem:hover { background-color: #fb923c; color: white !important; }
  [data-theme="daylight"] .dropdown-item-separator { border-top-color: #e0e0e0; }
  [data-theme="daylight"] .level-btn, [data-theme="daylight"] .level-btn.btn-link { color: #888888; }
  [data-theme="daylight"] .level-btn:hover, [data-theme="daylight"] .level-btn.btn-link:hover { color: #666666; }
  [data-theme="daylight"] .level-btn.active, [data-theme="daylight"] .level-btn.btn-link.active { color: #1a1a1a; border-bottom-color: #f97316 !important; }
  [data-theme="daylight"] .level-btn:disabled { color: #cccccc; }
  [data-theme="daylight"] .desktop-nav-btn { background-color: #ffffff; color: #1a1a1a; border: 1px solid #e0e0e0; }
  [data-theme="daylight"] .desktop-nav-btn:hover { background-color: #f5f5f5; border-color: #f97316; }
  [data-theme="daylight"] .city-report-btn { background-color: #ffffff; color: #1a1a1a; border: 1px solid #e0e0e0; }
  [data-theme="daylight"] .city-report-btn:hover { background-color: #f5f5f5; border-color: #f97316; }
  [data-theme="daylight"] .city-search-dropdown-item:hover { background-color: #fb923c; color: white; }
  [data-mode="light"] #docsDropdownItem:hover { background-color: #0088cc; color: white !important; }
  [data-mode="light"] .dropdown-item-separator { border-top-color: var(--border-primary); }
  [data-mode="light"] .level-btn, [data-mode="light"] .level-btn.btn-link { color: var(--text-tertiary); }
  [data-mode="light"] .level-btn:hover, [data-mode="light"] .level-btn.btn-link:hover { color: #4A4A4A; }
  [data-mode="light"] .level-btn.active, [data-mode="light"] .level-btn.btn-link.active { color: var(--text-primary); border-bottom-color: var(--accent-primary) !important; }
  [data-mode="light"] .level-btn:disabled { color: var(--text-muted); }
  [data-mode="light"] .desktop-nav-btn { background-color: var(--bg-card); color: var(--text-primary); border: 1px solid var(--border-primary); }
  [data-mode="light"] .desktop-nav-btn:hover { background-color: var(--bg-primary); border-color: var(--accent-primary); }
  [data-mode="light"] .city-report-btn { background-color: var(--bg-card); color: var(--text-primary); border: 1px solid var(--border-primary); }
  [data-mode="light"] .city-report-btn:hover { background-color: var(--bg-primary); border-color: var(--accent-primary); }
  [data-mode="light"] .city-search-dropdown-item:hover { background-color: #0088cc; color: white; }
  [data-mode="dark"] #docsDropdownItem:hover { background-color: var(--accent-primary); color: var(--bg-primary) !important; }
  [data-mode="dark"] .dropdown-item-separator { border-top-color: rgba(255, 255, 255, 0.08); }
  [data-mode="dark"] .level-btn, [data-mode="dark"] .level-btn.btn-link { color: var(--text-muted); }
  [data-mode="dark"] .level-btn:hover, [data-mode="dark"] .level-btn.btn-link:hover { color: var(--text-tertiary); }
  [data-mode="dark"] .level-btn.active, [data-mode="dark"] .level-btn.btn-link.active { color: var(--text-primary); border-bottom-color: var(--accent-primary) !important; }
  [data-mode="dark"] .level-btn:disabled { color: #505050; }
  [data-mode="dark"] .desktop-nav-btn { background-color: #1E1E1E; color: var(--text-primary); border: 1px solid rgba(255, 255, 255, 0.1); }
  [data-mode="dark"] .desktop-nav-btn:hover { background-color: var(--bg-button-hover); border-color: var(--accent-primary); }
  [data-mode="dark"] .city-report-btn { background-color: #1E1E1E; color: var(--text-primary); border: 1px solid rgba(255, 255, 255, 0.1); }
  [data-mode="dark"] .city-report-btn:hover { background-color: var(--bg-button-hover); border-color: var(--accent-primary); }
  [data-mode="dark"] .city-search-dropdown-item:hover { background-color: var(--accent-primary); color: var(--bg-primary); }
`;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => { document.head.removeChild(styleElement); };
  }, []);

  // Measure the fixed navbar and keep the spacer in sync. offsetHeight ignores
  // the translateY hide transform, so it stays correct while scrolling, and
  // ResizeObserver catches the level switcher row appearing/disappearing.
  useEffect(() => {
    const el = navbarRef.current;
    if (!el) return;
    const update = () => {
      const last = el.lastElementChild || el;
      setNavHeight(last.offsetTop + last.offsetHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  const fallbackCountryConfig = {
    us: { displayName: 'United States', flag: '🇺🇸' },
    ca: { displayName: 'Canada', flag: '🇨🇦' },
    canada: { displayName: 'Canada', flag: '🇨🇦' },
    mx: { displayName: 'Mexico', flag: '🇲🇽' },
    mexico: { displayName: 'Mexico', flag: '🇲🇽' },
    uk: { displayName: 'United Kingdom', flag: '🇬🇧' },
    australia: { displayName: 'Australia', flag: '🇦🇺' },
    france: { displayName: 'France', flag: '🇫🇷' },
    italy: { displayName: 'Italy', flag: '🇮🇹' },
    japan: { displayName: 'Japan', flag: '🇯🇵' },
    'south-africa': { displayName: 'South Africa', flag: '🇿🇦' },
    ukraine: { displayName: 'Ukraine', flag: '🇺🇦' },
    all: { displayName: 'All Countries', flag: '🌐' }
  };

  const getCountryOptions = () => {
    const activeCountries = getActiveCountries();
    const usCountry = { slug: 'us', displayName: 'United States', flag: '🇺🇸' };
    const hasUS = activeCountries.some(country => country.slug === 'us');
    let allCountries = hasUS ? activeCountries : [usCountry, ...activeCountries];
    const getPriorityIndex = (slug) => {
      if (slug === 'ca' || slug === 'canada') return 0;
      if (slug === 'mx' || slug === 'mexico') return 1;
      if (slug === 'uk') return 2;
      if (slug === 'us') return 3;
      return -1;
    };
    const sortedCountries = allCountries.sort((a, b) => {
      const aPriority = getPriorityIndex(a.slug);
      const bPriority = getPriorityIndex(b.slug);
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
    return [
      { slug: 'all', displayName: 'All Countries', flag: '🌐', isSpecial: true, searchSchema: 'all_countries' },
      ...sortedCountries.map(country => ({ ...country, searchSchema: getSearchSchema(country.slug) }))
    ];
  };

  const getSearchSchema = (countrySlug) => {
    const schemaMap = {
      'us': 'united_states_of_america', 'ca': 'canada', 'canada': 'canada',
      'mx': 'mexico', 'mexico': 'mexico', 'uk': 'united_kingdom',
      'australia': 'australia', 'china': 'china', 'france': 'france',
      'germany': 'germany', 'israel': 'israel', 'italy': 'italy',
      'japan': 'japan', 'nigeria': 'nigeria', 'south-africa': 'south_africa', 'ukraine': 'ukraine'
    };
    return schemaMap[countrySlug] || countrySlug;
  };

  const effectiveCountry = currentCountry || 'us';

  const getCurrentCountryInfo = () => {
    if (effectiveCountry === 'all') return { displayName: 'All Countries', flag: '🌐' };
    const info = getCountryInfo(effectiveCountry);
    if (info) return { displayName: info.displayName || info.name, flag: info.flag };
    if (fallbackCountryConfig[effectiveCountry]) return fallbackCountryConfig[effectiveCountry];
    return { displayName: effectiveCountry.charAt(0).toUpperCase() + effectiveCountry.slice(1).replace(/-/g, ' '), flag: '🌐' };
  };

  const countryOptions = getCountryOptions();
  const currentCountryInfo = getCurrentCountryInfo();

  const getDropdownOptions = useMemo(() => {
    if (isOnLocalPage && effectiveCountry === 'us') {
      return { type: 'local', options: [], currentDisplay: homeCityAbbreviation || 'LOC', displayType: 'text' };
    }
    if (selectedRegion) {
      if (currentLevel === 'state' && effectiveCountry === 'us') {
        return { type: 'us-state', options: US_STATES_DATA, currentDisplay: selectedRegion.flag, displayType: 'image' };
      }
      if (currentLevel === 'province' && (effectiveCountry === 'ca' || effectiveCountry === 'canada')) {
        return { type: 'ca-province', options: CANADA_PROVINCES_DATA, currentDisplay: selectedRegion.flag, displayType: 'image' };
      }
      if (currentLevel === 'state' && (effectiveCountry === 'mx' || effectiveCountry === 'mexico')) {
        return { type: 'mx-state', options: MEXICO_STATES_DATA, currentDisplay: selectedRegion.flag, displayType: 'image' };
      }
      if (currentLevel === 'kingdom' && effectiveCountry === 'uk') {
        return { type: 'uk-kingdom', options: UK_KINGDOMS_DATA, currentDisplay: selectedRegion.flag, displayType: 'image' };
      }
    }
    return { type: 'country', options: countryOptions, currentDisplay: currentCountryInfo.flag, displayType: 'emoji' };
  }, [currentLevel, effectiveCountry, countryOptions, currentCountryInfo, selectedRegion, homeCityAbbreviation, isOnLocalPage]);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedCountry = localStorage.getItem('selectedCountry') || localStorage.getItem('homeCountry');
      if (storedCountry && (fallbackCountryConfig[storedCountry] || getCountryInfo(storedCountry))) {
        setCurrentCountry(storedCountry);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    handleStorageChange();
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/fact-check')) {
      setCurrentCountry('us');
      localStorage.setItem('selectedCountry', 'us');
      return;
    }
    const neutralPaths = ['/notifications/settings', '/collection', '/notifications'];
    const isNeutralPage = neutralPaths.some(p => path.startsWith(p));
    if (isNeutralPage) {
      const storedCountry = localStorage.getItem('selectedCountry') || localStorage.getItem('homeCountry');
      if (storedCountry) { setCurrentCountry(storedCountry); return; }
    }
    const allCountrySlugs = getActiveCountries().map(c => c.slug);
    allCountrySlugs.push('us');
    Object.keys(fallbackCountryConfig).forEach(slug => { if (!allCountrySlugs.includes(slug)) allCountrySlugs.push(slug); });
    allCountrySlugs.sort((a, b) => b.length - a.length);
    for (const slug of allCountrySlugs) {
      if (path.startsWith(`/${slug}`) || (slug === 'us' && path === '/')) {
        setCurrentCountry(slug);
        localStorage.setItem('selectedCountry', slug);
        return;
      }
    }
    if (path.startsWith('/all')) { setCurrentCountry('all'); localStorage.setItem('selectedCountry', 'all'); return; }
    const storedCountry = localStorage.getItem('selectedCountry') || localStorage.getItem('homeCountry');
    if (storedCountry) { setCurrentCountry(storedCountry); return; }
    setCurrentCountry('us');
  }, [location.pathname]);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > lastScrollY && currentScrollY > 100) setShowNavbar(false);
    else if (currentScrollY < lastScrollY) setShowNavbar(true);
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    let timeoutId = null;
    const throttledHandleScroll = () => {
      if (timeoutId === null) {
        timeoutId = setTimeout(() => { handleScroll(); timeoutId = null; }, 10);
      }
    };
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => { window.removeEventListener('scroll', throttledHandleScroll); if (timeoutId) clearTimeout(timeoutId); };
  }, [handleScroll]);

  const runSearch = useCallback(() => {
    if (searchQuery.trim() === '') return;
    localStorage.setItem('previousPageUrl', location.pathname);
    const encodedQuery = encodeURIComponent(searchQuery.trim());
    const currentCountryOption = countryOptions.find(option => option.slug === effectiveCountry);
    const searchSchema = currentCountryOption?.searchSchema || getSearchSchema(effectiveCountry) || 'united_states_of_america';
    navigate(`/search/${searchSchema}?query=${encodedQuery}&limit=10`);
  }, [searchQuery, navigate, location.pathname, effectiveCountry, countryOptions]);

  const handleFormSubmit = useCallback((e) => { e.preventDefault(); runSearch(); }, [runSearch]);
  const handleKeyDown = useCallback((e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }, [runSearch]);

  const handleHapticsClick = useCallback(async (callback) => {
    try { await Haptics.impact({ style: ImpactStyle.Light }); } catch (error) { console.debug('Haptics not available'); }
    callback();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCountryChange = useCallback((countrySlug) => {
    localStorage.setItem('selectedCountry', countrySlug);
    setCurrentLevel('federal');
    localStorage.setItem('selectedLevel', 'federal');
    navigate(countrySlug === 'us' ? '/us' : `/${countrySlug}`);
  }, [navigate]);

  const handleRegionChange = useCallback((region) => {
    setSelectedRegion(region);
    navigate(region.path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const handleLevelChange = useCallback(async (level) => {
    try { await Haptics.impact({ style: ImpactStyle.Light }); } catch (error) { console.debug('Haptics not available'); }
    setCurrentLevel(level);
    localStorage.setItem('selectedLevel', level);
    if (level === 'federal') {
      let targetPath = '/us';
      if (effectiveCountry === 'ca' || effectiveCountry === 'canada') targetPath = '/canada';
      else if (effectiveCountry === 'mx' || effectiveCountry === 'mexico') targetPath = '/mexico';
      else if (effectiveCountry === 'uk') targetPath = '/uk';
      else if (effectiveCountry !== 'us') targetPath = `/${effectiveCountry}`;
      navigate(targetPath);
    } else if (level === 'state') {
      if (effectiveCountry === 'us') {
        if (selectedRegion) navigate(selectedRegion.path);
        else if (homeStateInfo) navigate(`/us/${homeStateInfo.fullName.toLowerCase().replace(/\s+/g, '')}`);
      } else if (effectiveCountry === 'mx' || effectiveCountry === 'mexico') {
        if (selectedRegion) navigate(selectedRegion.path);
        else if (homeMexicoStateInfo) navigate(`/mexico/${homeMexicoStateInfo.key || homeMexicoStateInfo.name?.toLowerCase().replace(/\s+/g, '-')}`);
      }
    } else if (level === 'province') {
      if (selectedRegion) navigate(selectedRegion.path);
      else if (homeProvinceInfo) navigate(`/canada/${homeProvinceInfo.key || homeProvinceInfo.name?.toLowerCase().replace(/\s+/g, '-')}`);
    } else if (level === 'kingdom') {
      if (selectedRegion) navigate(selectedRegion.path);
      else if (homeKingdomInfo) navigate(`/uk/${homeKingdomInfo.key || homeKingdomInfo.name?.toLowerCase().replace(/\s+/g, '-')}`);
    } else if (level === 'local') {
      navigate('/us/local');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [effectiveCountry, selectedRegion, homeStateInfo, homeProvinceInfo, homeMexicoStateInfo, homeKingdomInfo, navigate]);

  const handleDesktopNavClick = useCallback((path, external = false) => {
    if (external) window.open(path, '_blank');
    else { navigate(path); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  }, [navigate]);

  const isNativeApp = Capacitor.isNativePlatform();
  const showUSLevelSwitcher = effectiveCountry === 'us';
  const showCanadaLevelSwitcher = effectiveCountry === 'ca' || effectiveCountry === 'canada';
  const showMexicoLevelSwitcher = effectiveCountry === 'mx' || effectiveCountry === 'mexico';
  const showUKLevelSwitcher = effectiveCountry === 'uk';
  const showLevelSwitcher = showUSLevelSwitcher || showCanadaLevelSwitcher || showMexicoLevelSwitcher || showUKLevelSwitcher;

  const getSearchPlaceholder = () => {
    if (isOnLocalPage && homeCityName) return t('common:searchLocation', 'Search {{name}}', { name: homeCityName });
    if (selectedRegion) return t('common:searchLocation', 'Search {{name}}', { name: selectedRegion.name });
    return t('common:searchLocation', 'Search {{name}}', { name: currentCountryInfo.displayName });
  };

  const renderLevelSwitcher = () => {
    if (showUSLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${currentLevel === 'federal' ? 'active' : ''}`} onClick={() => handleLevelChange('federal')} aria-label={t('common:levelFederal', 'Federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${currentLevel === 'state' ? 'active' : ''}`} onClick={() => handleLevelChange('state')} disabled={!homeStateInfo && !selectedRegion} aria-label={homeStateInfo ? `${t('common:levelState', 'State')}: ${homeStateInfo.fullName || homeStateInfo.key}` : t('common:setHomeState', 'Set home state')} title={homeStateInfo?.fullName || t('common:setHomeStateInSettings', 'Set home state in settings')}>{t('common:levelState', 'State')}</Button>
          <Button variant="link" className={`level-btn ${currentLevel === 'local' ? 'active' : ''}`} onClick={() => handleLevelChange('local')} disabled={!homeCityName} aria-label={homeCityName ? `${t('common:levelLocal', 'Local')}: ${homeCityName}` : t('common:setHomeCity', 'Set home city')} title={homeCityName || t('common:setHomeCityInSettings', 'Set home city in settings')}>{t('common:levelLocal', 'Local')}</Button>
        </div>
      );
    }
    if (showCanadaLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${currentLevel === 'federal' ? 'active' : ''}`} onClick={() => handleLevelChange('federal')} aria-label={t('common:levelFederal', 'Federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${currentLevel === 'province' ? 'active' : ''}`} onClick={() => handleLevelChange('province')} disabled={!homeProvinceInfo} aria-label={homeProvinceInfo ? `${t('common:levelProvince', 'Province')}: ${homeProvinceInfo.name}` : t('common:setHomeProvince', 'Set home province')} title={homeProvinceInfo?.name || t('common:setHomeProvinceInSettings', 'Set home province in settings')}>{t('common:levelProvince', 'Province')}</Button>
        </div>
      );
    }
    if (showMexicoLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${currentLevel === 'federal' ? 'active' : ''}`} onClick={() => handleLevelChange('federal')} aria-label={t('common:levelFederal', 'Federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${currentLevel === 'state' ? 'active' : ''}`} onClick={() => handleLevelChange('state')} disabled={!homeMexicoStateInfo} aria-label={homeMexicoStateInfo ? `${t('common:levelState', 'State')}: ${homeMexicoStateInfo.name}` : t('common:setHomeState', 'Set home state')} title={homeMexicoStateInfo?.name || t('common:setHomeStateInSettings', 'Set home state in settings')}>{t('common:levelState', 'State')}</Button>
        </div>
      );
    }
    if (showUKLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${currentLevel === 'federal' ? 'active' : ''}`} onClick={() => handleLevelChange('federal')} aria-label={t('common:levelFederal', 'Federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${currentLevel === 'kingdom' ? 'active' : ''}`} onClick={() => handleLevelChange('kingdom')} disabled={!homeKingdomInfo} aria-label={homeKingdomInfo ? `${t('common:levelKingdom', 'Kingdom')}: ${homeKingdomInfo.name}` : t('common:setHomeRegion', 'Set home region')} title={homeKingdomInfo?.name || t('common:setHomeRegionInSettings', 'Set home region in settings')}>{t('common:levelKingdom', 'Kingdom')}</Button>
        </div>
      );
    }
    return null;
  };

  const renderDropdownToggle = () => {
    const { currentDisplay, displayType } = getDropdownOptions;
    if (displayType === 'emoji') return currentDisplay;
    if (displayType === 'image') return <Image src={currentDisplay} alt="Current region flag" className="dropdown-flag-img" />;
    if (displayType === 'text') return <span className="city-abbreviation">{currentDisplay}</span>;
    return currentDisplay;
  };

  const renderDropdownItems = () => {
    const { type, options } = getDropdownOptions;
    if (type === 'local') return <Dropdown.Item id="docsDropdownItem" disabled>{homeCityName || t('common:noCitySelected', 'No city selected')}</Dropdown.Item>;
    if (type === 'country') {
      return options.map((country, index) => (
        <React.Fragment key={country.slug}>
          {index === 1 && <Dropdown.Divider className="dropdown-item-separator" />}
          <Dropdown.Item id="docsDropdownItem" className={`${country.isSpecial ? 'dropdown-item-special' : ''} ${effectiveCountry === country.slug ? 'selected' : ''}`} onClick={() => handleHapticsClick(() => handleCountryChange(country.slug))}>
            <span role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCountryChange(country.slug); } }}>
              {country.flag} {country.displayName}
            </span>
          </Dropdown.Item>
        </React.Fragment>
      ));
    }
    return options.map((region) => (
      <Dropdown.Item key={region.key} id="docsDropdownItem" onClick={() => handleHapticsClick(() => handleRegionChange(region))} className={selectedRegion?.key === region.key ? 'selected' : ''}>
        <Image src={region.flag} alt={`${region.name} flag`} className="dropdown-region-flag" />
        <span>{region.name}</span>
      </Dropdown.Item>
    ));
  };

  const renderLeftElement = () => {
    if (isOnLocalPage && effectiveCountry === 'us') {
      return (
        <button 
          className="city-report-btn"
          onClick={() => setCitySearchModalOpen(true)}
          aria-label={t('common:generateCityReport', 'Generate city report')}
          title={t('common:generateCityReport', 'Generate city report')}
        >
          <Newspaper />
        </button>
      );
    }
    return (
      <Dropdown id="mobileDropdown" align="start">
        <Dropdown.Toggle id="searchCountry">{renderDropdownToggle()}</Dropdown.Toggle>
        <Dropdown.Menu id="docsDropdown">{renderDropdownItems()}</Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <>
      <Navbar id="navbarMobileTop" fixed="top" className={`navbar-container ${showNavbar ? 'show' : 'hide'}`}>
        <Form className="text-center d-flex align-items-center w-100" onSubmit={handleFormSubmit} role="search">
          <Form.Group ref={navbarRef} id="searchContainer" className="d-flex flex-grow-1">
            <div className="search-row">
              {renderLeftElement()}
              <Form.Control id="ggSearch2" placeholder={getSearchPlaceholder()} type="search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={handleKeyDown} autoComplete="off" aria-label={getSearchPlaceholder()} />
              <div className="desktop-nav-buttons">
                <Button className="desktop-nav-btn" onClick={() => handleDesktopNavClick('/about')} aria-label={t('common:about', 'About')}><Info /><span>{t('common:about', 'About')}</span></Button>
                <Button className="desktop-nav-btn" onClick={() => handleDesktopNavClick('https://donate.stripe.com/28o3fXbdvbQO2RO288', true)} aria-label={t('common:donate', 'Donate')}><HeartFill className="donate-heart" /><span>{t('common:donate', 'Donate')}</span></Button>
                <Button className="desktop-nav-btn" onClick={() => handleDesktopNavClick('/collection')} aria-label={t('common:collection', 'Collection')}><Folder /><span>{t('common:collection', 'Collection')}</span></Button>
                <Button className="desktop-nav-btn" onClick={() => handleDesktopNavClick('https://apps.apple.com/us/app/gov-glance/id6448072618', true)} aria-label={t('common:downloadApp', 'Download App')}><Phone /><span>{t('common:app', 'App')}</span></Button>
              </div>
              <div className="navbar-nav-container"><MobileNavigation currentCountry={effectiveCountry} /></div>
            </div>
            {showLevelSwitcher && <div className="level-switcher-row">{renderLevelSwitcher()}</div>}
          </Form.Group>
        </Form>
      </Navbar>
      <div className="navbar-spacer" style={{ height: navHeight }} />

      {/* City Search Modal */}
      <Modal show={citySearchModalOpen} onHide={closeCitySearchModal} size="lg" centered className="city-search-modal">
        <Modal.Header className="city-search-modal-header" closeButton>
          <h3><Building /> {t('common:findACity', 'Find a City')}</h3>
        </Modal.Header>
        <Modal.Body className="city-search-modal-body">
          <div className="city-search-input-wrapper">
            <input
              type="text"
              value={citySearchQuery}
              onChange={handleCitySearchChange}
              onKeyDown={handleCitySearchKeyDown}
              onFocus={() => citySearchResults.length > 0 && setShowCityDropdown(true)}
              placeholder={t('common:typeCityName', 'Type a city name...')}
              className={`city-search-input ${showCityDropdown ? 'dropdown-open' : ''}`}
              autoFocus
            />
            {searchingCities && (
              <div className="city-search-loading">{t('common:searching', 'Searching...')}</div>
            )}
            {showCityDropdown && !searchingCities && citySearchResults.length > 0 && (
              <div className="city-search-dropdown">
                {citySearchResults.map((city, index) => (
                  <button
                    key={`${city.name}_${city.state}_${index}`}
                    onClick={() => handleCitySelectForReport(city)}
                    className="city-search-dropdown-item"
                  >
                    <span>
                      <span style={{ fontWeight: "500" }}>{city.name}</span>
                      <span style={{ opacity: 0.7 }}>, {city.stateName}</span>
                    </span>
                    <span style={{ fontSize: "12px", opacity: 0.5 }}>{city.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {searchError && (<div className="city-search-error">{searchError}</div>)}
          <div className="city-search-helper">
            {citySearchQuery.length < 2 ? (
              <p>{t('common:startTypingCity', 'Start typing to search for a city')}</p>
            ) : !searchingCities && citySearchResults.length === 0 && citySearchQuery.length >= 2 ? (
              <p>{t('common:cityNoResults', 'No cities found matching "{{query}}"', { query: citySearchQuery })}</p>
            ) : null}
          </div>
        </Modal.Body>
      </Modal>

      {/* City Report Modal */}
      <Modal show={reportModalOpen} onHide={closeReportModal} size="xl" centered className="report-modal" scrollable>
        <Modal.Header className="report-modal-header" closeButton>
          <h3><Newspaper /> {t('common:cityReport', '{{city}} Report', { city: selectedReportCity?.name })}</h3>
        </Modal.Header>
        <Modal.Body className="report-modal-body">
          {reportLoading ? (
            <div className="report-loading">
              <Spinner animation="border" variant="primary" />
              <span>{t('common:generatingReport', 'Generating report for {{city}}...', { city: selectedReportCity?.name })}</span>
            </div>
          ) : reportError ? (
            <div className="report-error">
              <p>{reportError}</p>
              <Button variant="primary" onClick={() => selectedReportCity && handleCitySelectForReport(selectedReportCity)}>
                {t('common:tryAgain', 'Try Again')}
              </Button>
            </div>
          ) : reportData ? (
            <div className="report-content">
              <div className="report-tab-buttons">
                <Button className={`report-tab-btn ${reportModalView === 'summary' ? 'active' : ''}`} onClick={() => setReportModalView('summary')}>
                  <FileText className="me-1" /> {t('common:report', 'Report')}
                </Button>
                <Button className={`report-tab-btn ${reportModalView === 'sources' ? 'active' : ''}`} onClick={() => setReportModalView('sources')}>
                  <List className="me-1" /> {t('common:sourcesCount', 'Sources ({{count}})', { count: reportData.citations?.length || 0 })}
                </Button>
              </div>
              <div className="report-meta-info">
                <small>
                  {reportData.generatedAt}
                  <span> • {reportData.city}, {reportData.state}</span>
                </small>
              </div>
              {reportModalView === 'summary' && (
                reportData.content ? (
                  <ReportContent content={reportData.content} citations={reportData.citations} className="report-modal-content" />
                ) : (
                  <p className="report-no-content">{t('common:noSummaryForReport', 'No summary available for this report.')}</p>
                )
              )}
              {reportModalView === 'sources' && (
                <>
                  {reportData.citations && reportData.citations.length > 0 ? (
                    reportData.citations.map((citation, index) => (
                      <div key={`citation-${citation.url}-${index}`} className="report-citation-card">
                        <Card>
                          <Card.Body onClick={() => openCapacitorSite(citation.url, '_blank')} style={{ cursor: 'pointer' }}>
                            <div className="citation-title">{citation.title}</div>
                            {citation.date && (
                              <div className="citation-date">{new Date(citation.date).toLocaleDateString()}</div>
                            )}
                          </Card.Body>
                          <Card.Footer className="d-flex">
                            <Button className="clickable me-2" id="billLink" onClick={() => openCapacitorSite(citation.url, '_blank')}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
                            </Button>
                          </Card.Footer>
                        </Card>
                      </div>
                    ))
                  ) : (
                    <p className="report-no-content">{t('common:noSourcesForReport', 'No sources available for this report.')}</p>
                  )}
                </>
              )}
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SearchNavBar;